import { MapState } from "@/store/map/types";
import {
	DisplayItem,
	IMapEventListener,
	MapEventListenerEventNames,
	MapLayerItemCustomType,
	MarkerContextMenuResponse,
	MarkerServiceOptions,
	VisibleMarkerKeysResponse
} from "@sureview/v2-mapping-saas";

import { Md5 } from "ts-md5";
import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";

const MapModule = namespace("map");

/**
 * @summary Mixin which handles the map markers.
 */
@Component
export default class MapMarkerMixin extends Vue {
    @MapModule.State((state: MapState) => state.visibleMarkerKeysResponse) public readonly visibleMarkerKeysResponse: VisibleMarkerKeysResponse | null;
    @MapModule.Mutation protected setMarkerContextMenuResponse: (markerContextMenuResponse: MarkerContextMenuResponse | null) => void;
    @MapModule.Mutation protected setVisibleMarkerKeysResponse: (visibleMarkerKeysResponse: VisibleMarkerKeysResponse | null) => void;
    @MapModule.Getter private getCustomMarkerData: (displayMapLayerItemTypeId: number) => string;
    @MapModule.Getter private getCustomMarkerElement: (displayMapLayerItemTypeId: number) => Node;
    @MapModule.Getter protected getCustomType: (displayMapLayerItemTypeId: number) => MapLayerItemCustomType;

    /**
     * @summary The markers to be displayed on the map.
     */
    public markers: DisplayItem[] = [];

    /**
     * @summary The marker service map event listeners.
     */
    public markerServiceMapEventListeners: IMapEventListener[] = [];

    /**
     * @summary The marker service options.
     * @returns {MarkerServiceOptions} Returns the marker service options.
     */
    public get markerServiceOptions(): MarkerServiceOptions {
        return {
            mapEventListeners: this.markerServiceMapEventListeners,
        };
    }

    /**
     * @summary Creates the markers for the map.
     * @param {DisplayItem} marker The details of the marker.
     * @returns {DisplayItem} Returns the marker.
     */
    public createMarker(marker: DisplayItem): DisplayItem {
        const customType: MapLayerItemCustomType = marker.displayMapLayerItemTypeId
                    ? this.getCustomType(marker.displayMapLayerItemTypeId)
                    : MapLayerItemCustomType.None;

        let displayItem: DisplayItem = {
            key: marker.key,
            typeId: marker.typeId,
            title: marker.title,
            position: marker.position,
            mapLayerItemId: marker.mapLayerItemId,
            state: marker.state,
            regionPath: marker.regionPath,
            customIconForMarker: marker.displayMapLayerItemTypeId
                ? this.getCustomMarkerData(marker.displayMapLayerItemTypeId)
                : null,
            customMarkerElement: marker.displayMapLayerItemTypeId
                ? this.getCustomMarkerElement(marker.displayMapLayerItemTypeId)
                : null,
            customType: marker.displayMapLayerItemTypeId
                ? this.getCustomType(marker.displayMapLayerItemTypeId)
                : MapLayerItemCustomType.None,
            minElevation: marker.minElevation,
            maxElevation: marker.maxElevation,
            displayMapLayerItemTypeId: customType === MapLayerItemCustomType.None ? null : marker.displayMapLayerItemTypeId,
            iconClass: marker.iconClass,
            objectId: marker.objectId,
			      groupId: marker.groupId,
            mapEventListeners: marker.mapEventListeners ?? [],
        };

        this.setContextMenuEventListener(displayItem);
        displayItem.key = Md5.hashStr(JSON.stringify(displayItem));

        return displayItem;
    }

    /**
     * @summary Sets the visible marker keys event listener.
     */
    public setVisibleMarkerKeysEventListener(): void {
        this.markerServiceMapEventListeners.push({
            eventName: MapEventListenerEventNames.VisibleKeys, callback: this.setVisibleMarkerKeysResponse,
        });
    }

	/**
	 * @summary Adds the context-menu event listener.
	 * @param {DisplayItem} marker The details of the marker.
	 */
	private setContextMenuEventListener(marker: DisplayItem): void {
		marker.mapEventListeners.push({
			eventName: MapEventListenerEventNames.ContextMenu,
			callback: this.setMarkerContextMenuResponse,
		});
	}
}
